import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useContent } from "../context/ContentContext";
import { fetchProduct, fetchProducts, resolveImg } from "../services/api";
import ProductCard from "../components/ProductCard";
import { Button } from "../components/ui/button";
import { CheckCircle2, ArrowRight, Truck, ShieldCheck, Clock, Boxes, Loader2 } from "lucide-react";

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { categories } = useContent();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [activeImg, setActiveImg] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchProduct(id)
      .then((p) => {
        setProduct(p);
        setActiveImg(p.image);
        return fetchProducts(p.category);
      })
      .then((all) => setRelated(all.filter((x) => x.id !== id).slice(0, 4)))
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return <div className="py-32 flex justify-center"><Loader2 className="w-8 h-8 text-[#C9A227] animate-spin" /></div>;
  }

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-32 text-center">
        <h1 className="font-display font-bold text-2xl">Product not found</h1>
        <Link to="/" className="text-[#C9A227] mt-3 inline-block">Back to Home</Link>
      </div>
    );
  }

  const category = categories.find((c) => c.slug === product.category);
  const gallery = [product.image, ...(product.images || [])].filter(Boolean);
  const highlights = [
    { icon: Boxes, label: product.moq },
    { icon: Clock, label: `Lead time ${product.leadTime}` },
    { icon: Truck, label: "Worldwide shipping" },
    { icon: ShieldCheck, label: "Quality guaranteed" },
  ];

  return (
    <div className="py-10">
      <div className="max-w-7xl mx-auto px-6">
        <nav className="text-sm text-neutral-500 mb-6">
          <Link to="/" className="hover:text-[#C9A227]">Home</Link>
          <span className="mx-2">/</span>
          <Link to={`/category/${product.category}`} className="hover:text-[#C9A227]">{category?.name}</Link>
          <span className="mx-2">/</span>
          <span className="text-[#14171c]">{product.name}</span>
        </nav>

        <div className="grid lg:grid-cols-2 gap-10">
          <div>
            <div className="rounded-2xl overflow-hidden border border-neutral-100 bg-neutral-50">
              <img src={resolveImg(activeImg)} alt={product.name} className="w-full h-full object-cover aspect-square" />
            </div>
            {gallery.length > 1 && (
              <div className="flex gap-3 mt-4 flex-wrap">
                {gallery.map((img, i) => (
                  <button key={i} onClick={() => setActiveImg(img)}
                    className={`w-20 h-20 rounded-lg overflow-hidden border-2 ${activeImg === img ? "border-[#C9A227]" : "border-transparent"}`}>
                    <img src={resolveImg(img)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="text-[#C9A227] font-bold text-sm uppercase tracking-widest mb-2">{product.subcategory}</div>
            <h1 className="font-display font-extrabold text-3xl md:text-4xl text-[#14171c] leading-tight">{product.name}</h1>
            <p className="text-neutral-600 leading-relaxed mt-5">{product.description}</p>
            <div className="mt-6 space-y-2.5">
              {(product.features || []).map((f) => (
                <div key={f} className="flex items-center gap-2.5"><CheckCircle2 className="w-5 h-5 text-[#C9A227] flex-shrink-0" /><span className="text-neutral-700">{f}</span></div>
              ))}
            </div>
            <div className="mt-7 p-5 rounded-xl bg-neutral-50 border border-neutral-100">
              <div className="text-sm text-neutral-500 mb-1">Material</div>
              <div className="font-semibold text-[#14171c]">{product.material}</div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-5">
              {highlights.map((h) => (
                <div key={h.label} className="flex items-center gap-2.5 text-sm text-neutral-700"><h.icon className="w-5 h-5 text-[#C9A227]" /> {h.label}</div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3 mt-8">
              <Button onClick={() => navigate("/contact", { state: { product: product.name } })} className="bg-[#C9A227] hover:bg-[#A6841B] text-white rounded-full h-12 px-8">Request a Quote <ArrowRight className="w-4 h-4 ml-1" /></Button>
              <Button onClick={() => navigate(`/category/${product.category}`)} variant="outline" className="rounded-full h-12 px-8 border-neutral-300 hover:border-[#C9A227] hover:text-[#C9A227]">Back to {category?.short}</Button>
            </div>
          </div>
        </div>

        {related.length > 0 && (
          <div className="mt-20">
            <h2 className="font-display font-extrabold text-2xl text-[#14171c] mb-8">Related Products</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              {related.map((p) => (<ProductCard key={p.id} product={p} />))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
